import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { clearPersistedView } from './viewStorage';
import { clearActiveBusinessId, resolveInitialActiveBusiness, storeActiveBusinessId } from './businessContext';
import { getDefaultViewForAuthPayload } from './roleCapabilities';
import { getRoleHomePath } from './authRedirect';

const TOKEN_KEY = 'token';
// utils/proOnboarding.js scopes its progress keys by user (and business) id, so the
// only way to clear them on logout is to sweep this shared prefix.
const PRO_ONBOARDING_KEY_PREFIX = 'haastia.pro.onboarding.';
// JS timers cap delay to a signed 32-bit int (~24.8 days).
const MAX_TIMEOUT_MS = 2147483647;
let authExpiryTimeoutId = null;

const notifyAuthSessionChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('haastia.authSessionChanged'));
};

const ROLE_DEFAULT_VIEWS = Object.freeze({
  admin: 'admin',
  professional: 'professional',
  recruiter: 'professional',
  customer: 'customer',
});

const clearAuthExpiryTimeout = () => {
  if (authExpiryTimeoutId !== null) {
    clearTimeout(authExpiryTimeoutId);
    authExpiryTimeoutId = null;
  }
};

const scheduleAuthExpiry = ({ expirationTimeMs, navigate }) => {
  clearAuthExpiryTimeout();

  const scheduleNextCheck = () => {
    const remainingMs = expirationTimeMs - Date.now();

    if (remainingMs <= 0) {
      clearAuthStorage();
      if (navigate) {
        navigate('/login');
      }
      return;
    }

    authExpiryTimeoutId = setTimeout(
      scheduleNextCheck,
      Math.min(remainingMs, MAX_TIMEOUT_MS)
    );
  };

  scheduleNextCheck();
};

const clearProOnboardingStorage = () => {
  try {
    // Collect first: removing while iterating shifts the remaining key indices.
    const staleKeys = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && key.startsWith(PRO_ONBOARDING_KEY_PREFIX)) {
        staleKeys.push(key);
      }
    }
    staleKeys.forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    // Access to localStorage can throw in non-browser environments.
    console.error('Failed to clear onboarding storage', err);
  }
};

export const clearAuthStorage = () => {
  clearAuthExpiryTimeout();
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    // Access to localStorage can throw in non-browser environments.
    console.error('Failed to clear auth storage', err);
  }
  // Each of these guards its own storage access, so a throw above cannot skip them.
  clearPersistedView();
  clearActiveBusinessId();
  clearProOnboardingStorage();
};

/**
 * Pure read. This is called during render in ~25 components, so it must never
 * write to storage: clearing `currentView` mid-render used to flip the route
 * tree in AppRoutes, which remounted, read again, and cleared again. Callers
 * that own the auth *decision* use pruneInvalidAuth() from an effect instead.
 */
export const getValidToken = () => {
  let token;
  try {
    token = localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    console.error('Failed to read auth token', err);
    return null;
  }

  if (!token) return null;

  try {
    const payload = jwtDecode(token);
    if (!payload?.exp || payload.exp < Date.now() / 1000) {
      return null;
    }

    return { token, payload };
  } catch (err) {
    return null;
  }
};

/**
 * getValidToken() plus the eviction it used to do inline: drop the token and
 * every piece of state derived from it when there is no usable session. Only
 * safe to call from an effect or an event handler, never during render.
 */
export const pruneInvalidAuth = () => {
  const auth = getValidToken();
  if (!auth) {
    clearAuthStorage();
    return null;
  }
  return auth;
};

export const logoutSession = async () => {
  const auth = getValidToken();
  const token = auth?.token;
  try {
    await axios.post(
      '/api/auth/logout',
      {},
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
  } catch (err) {
    console.error('Failed to notify server about logout', err);
  } finally {
    clearAuthStorage();
    notifyAuthSessionChanged();
  }
};

/**
 * Persist an auth token, configure logout-on-expiry, and move the app to the
 * correct view/route. This is shared by the email+password login flow and the
 * Stripe checkout completion flow so both experiences stay consistent. Pass an
 * optional redirectPath to override the default landing page for the user.
 */
export const getDefaultAuthRedirect = (role, platformRole) => ({
  view: platformRole === 'platform_admin' ? 'admin' : ROLE_DEFAULT_VIEWS[role] || 'customer',
  defaultPath: getRoleHomePath(role, platformRole),
});

export const handleAuthSuccess = ({
  token,
  navigate,
  setCurrentView,
  redirectPath,
  navigateOnSuccess = true,
  user = null,
}) => {
  if (!token) {
    throw new Error('No token provided');
  }

  localStorage.setItem(TOKEN_KEY, token);

  const decodedToken = jwtDecode(token);
  if (!decodedToken?.exp) {
    clearAuthStorage();
    throw new Error('Token is missing an expiration');
  }

  const expirationTimeMs = decodedToken.exp * 1000;
  const remainingMs = expirationTimeMs - Date.now();

  if (remainingMs <= 0) {
    clearAuthStorage();
    throw new Error('Token already expired');
  }

  scheduleAuthExpiry({ expirationTimeMs, navigate });

  if (user) {
    storeActiveBusinessId(resolveInitialActiveBusiness(user)?._id || null);
  }
  notifyAuthSessionChanged();

  const role = decodedToken.role;
  const roleRedirect = getDefaultAuthRedirect(role, decodedToken.platformRole);
  const resolvedView = getDefaultViewForAuthPayload(decodedToken);
  const defaultPath = user?.workspaceLandingPath || roleRedirect.defaultPath;

  if (setCurrentView) {
    setCurrentView(resolvedView);
  }

  const destination = redirectPath || defaultPath;

  if (navigateOnSuccess && navigate && destination) {
    navigate(destination);
  }

  return decodedToken;
};
